import { useContext } from '@berlin-front/common/composables/context';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import {
  AtoneCapturePostRequestTransactionOptionsEnum,
  AtoneShopV2OptionGetResponse,
} from '@netprotections/berlin-bff';
import { BerlinBffContext } from '@services/BerlinBff';
import { ERROR_TYPE } from '@services/ModalParameterContext';
import { ref, watch, onBeforeUnmount, Ref, computed } from '@vue/composition-api';

import { set } from 'vue-gtag';

import { deferred, isAxiosError, notNil, errorToString } from '@berlin-front/common/utils';
import { broadcast } from '@/broadcast';
import { z } from 'zod';
import { ServiceDiscriminator } from '.';

type UseShopOptionParam = {
  /**
   * リトライ回数。
   */
  retryShopOptionGet?: number;
  /**
   * broadcastされるAPI_ERRORメッセージにマージされるメタデータ。
   */
  meta?: ServiceDiscriminator;
};

/**
 * shopOptionGetリクエストを複数のサービスタイプで共有するためのComposable。
 *
 * Atone,AtoneLiteはinit時に共にshopOptionを取得するが、会員サイト側が両方のサービスタイプを利用している場合、
 * 適当に実装すると２本、同じ要求を出してしまうため、これで一本化している。
 */
export function useShopOption(param?: UseShopOptionParam) {
  const { retryShopOptionGet, meta } = {
    retryShopOptionGet: 0,
    ...param,
  };
  const { shop } = useContext(BerlinBffContext);

  const paramRef = ref<
    | {
        pubKey: string;
        transactionOptions: AtoneCapturePostRequestTransactionOptionsEnum[];
      }
    | undefined
  >(undefined);

  /**
   * /shop/option APIの実行結果を保持します。
   */
  const shopOptionPromiseRef = ref<Promise<AtoneShopV2OptionGetResponse> | undefined>(undefined);

  // 要らなそう。
  // /**
  //  * /shop/optionの実行結果をクリアします。
  //  */
  // const reset = () => {
  //   paramRef.value = undefined;
  //   shopOptionPromiseRef.value = Promise.reject(new Error('未初期化で実行されています。'));
  // };

  /**
   * パラメータを指定して/shop/option apiを呼び出します。
   *
   * @param pubKey
   * @param transactionOptions
   */
  const shopOptionGet = (pubKey: string, transactionOptions: AtoneCapturePostRequestTransactionOptionsEnum[]) => {
    paramRef.value = { pubKey, transactionOptions };
  };

  const paramKey = computed<string | undefined>(() => JSON.stringify(paramRef.value));

  let abortController = new AbortController();

  watch(
    [paramKey] as const,
    ([to], [from]) => {
      // パラメータが変動したタイミングでリクエストを飛ばす。
      if (to && from !== to && paramRef.value) {
        const { pubKey, transactionOptions } = paramRef.value;
        // 直前のリクエストをabortする。
        abortController.abort();
        abortController = new AbortController();

        const [promise, resolve, reject] = deferred<AtoneShopV2OptionGetResponse>();
        shopOptionPromiseRef.value = promise;

        const abortSignal = abortController.signal;
        void (async () => {
          let retryable = true;
          let success = false;
          let count = 0;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let lastError: any;
          while (retryable && !success && count <= retryShopOptionGet) {
            try {
              if (count) {
                // リトライ時は待つ。
                // TODO: 作ってあったユーティリティが合わなかったのでゴリっと書いてるけど後でこの辺のリトライ処理を別の関数に切り出して。
                // eslint-disable-next-line no-await-in-loop, @typescript-eslint/no-loop-func
                await new Promise((r) => {
                  window.setTimeout(r, 1000 * 2 ** (count - 1));
                });
              }
              count += 1;
              // 直前にタイマー待ちしてるからリクエスト投げる前にabortされてないか確かめる必要はある。
              if (!abortSignal.aborted) {
                // eslint-disable-next-line no-await-in-loop
                const response = await shop.atoneShopControllerV2shopOptionGet(pubKey, transactionOptions, {
                  signal: abortSignal,
                  meta,
                });
                resolve(response.data);
                // GA4イベント送信
                set({
                  user_properties: {
                    shop_name: notNil(response.data.shopName),
                  },
                });
                success = true;
              }
            } catch (error) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              lastError = error as any;
              retryable = isAxiosError(error) && error.response === undefined;
            }
          }
          reject(lastError);
        })();
      }
    },
    { flush: 'sync', immediate: true },
  );

  onBeforeUnmount(() => {
    abortController.abort();
  });

  return [shopOptionGet, shopOptionPromiseRef] as const;
}

/**
 * prepaymentチェック
 * 決済の最終段階、ショップサイトのカートの中身とPaymentデータが食い違っていないかを検証する機能。
 * 例えば、決済モーダル起動中にカートの内容を更新した場合、カート不整合として決済を行わせたくない。
 *
 * チェック機能自体はbridgeで実装する。
 */
export function usePrepaymentCheck() {
  const abortController = new AbortController();
  const { signal } = abortController;
  onBeforeUnmount(() => abortController.abort());
  return async () => {
    const unlistenHandlers: (() => void)[] = [];
    const unlistenAll = () => {
      unlistenHandlers.splice(0).forEach((v) => v());
    };
    if (signal.aborted) return Promise.resolve(true);
    return new Promise<boolean>((resolve, reject) => {
      const onResponse = (response: { success: true; data: boolean } | { success: false }) => {
        unlistenAll();
        if (response.success) {
          resolve(response.data);
        } else {
          // 画面にはエラーってことだけ送る。なんもせず握り潰せばいいはず。
          reject(new Error('pre_paymentエラー'));
        }
      };
      const onAbort = () => {
        unlistenAll();
        resolve(false);
      };
      abortController.signal.addEventListener('abort', onAbort);

      // イベント後処理。
      unlistenHandlers.push(() => abortController.signal.removeEventListener('abort', onAbort));
      // メッセージの購読とイベント後処理。
      unlistenHandlers.push(broadcast.listen('PRE_PAYMENT_RESPONSE', onResponse));

      // pre_paymentの実行。
      broadcast.send({ type: 'PRE_PAYMENT_REQUEST' });
    });
  };
}

export const usePrepaymentCheckHandle = ({
  activeRef,
  errorRouteName,
  callback,
}: {
  activeRef: Ref<boolean>;
  errorRouteName: string;
  callback: () => Promise<boolean>;
}) => {
  const router = useRouter();
  const unlisten = broadcast.listen('PRE_PAYMENT_REQUEST', () => {
    if (!activeRef.value) return;
    void (async () => {
      try {
        const response = await callback();
        broadcast.send({
          type: 'PRE_PAYMENT_RESPONSE',
          payload: {
            success: true,
            data: response,
          },
        });
      } catch (e) {
        broadcast.send({
          type: 'PRE_PAYMENT_RESPONSE',
          payload: {
            success: false,
          },
        });
        const err =
          e instanceof Error
            ? { name: e.name, message: e.message, stack: e.stack }
            : { ...ERROR_TYPE.REQUEST_EXCEPTION, message: errorToString(e) };
        broadcast.send({
          type: 'JS_ERROR',
          payload: err,
        });
        // コールバックの実装が間違ってるってことだから店舗エラーとなる。
        void router.replace({ name: errorRouteName, params: { errorType: 'shop' } });
      }
    })();
  });
  onBeforeUnmount(unlisten);
};

/**
 * atone系サービスの初期化パラメータでlegacy指定されているかどうかを検査します。
 * @param param
 * @returns
 */
export const isLegacyParam = (param: unknown): boolean => {
  const result = z.object({ is_legacy: z.boolean() }).safeParse(param);
  if (result.success) {
    return result.data.is_legacy;
  }
  return false;
};
